import {
  OperationCreate,
  OperationGroup,
  OperationGroupCreate,
  OperationType,
  Pagination,
  TriggerCreate,
  TriggerType,
} from '@ekkogmbh/apisdk';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { convertCoordinateToPath } from '../../Common/Helper/Nodes';
import Permissions from 'src/Common/Permissions';
import { OperationForm } from './OperationForm';
import { OperationGroupCreateOverview } from './OperationGroupCreateOverview';
import { TriggerForm } from './TriggerForm';
import { ContentPanel, usePanel } from 'src/Common/Components/ContentControl/ContentPanel';
import NamedCoordinateDescription from 'src/Common/Components/Stepper/NameCoordinateDescription';
import useEkko from 'src/Common/Providers/EkkoProvider';
import { CenteredColumn } from 'src/Common/Components/Stacks';
import { StyledTextField } from 'src/Common/Components/Forms/StyledTextField';
import { CoordinateInput } from 'src/Common/Components/CoordinateInput';
import { StyledSelectField } from 'src/Common/Components/Forms/StyledSelectField';
import { Box } from '@mui/material';
import moment from 'moment';

interface Props {
  operationGroup?: OperationGroup;
  closeHandler: () => void;
  saveHandler: (operationGroup: OperationGroupCreate, csv?: File) => void;
}

export const OperationGroupPanel = ({ operationGroup, closeHandler, saveHandler }: Props): React.JSX.Element => {
  const { api, hasPermission } = useEkko();
  const { enableSave, enableNext, currentStep } = usePanel();
  const fetchLabels = useCallback((pagination: Pagination) => api.getLabels(pagination), [api]);

  const hasAreasReadPermission: boolean = useMemo(
    () => hasPermission(Permissions.OPERATION_GROUPS_READ),
    [hasPermission],
  );

  const [type, setType] = useState<OperationType>(OperationType.COMPARTMENT_UPDATE);
  const [file, setFile] = useState<File>();

  const [name, setName] = useState<string>('');
  const [coordinate, setCoordinate] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [triggers, setTriggers] = useState<TriggerCreate[]>([]);
  const [operations, setOperations] = useState<OperationCreate[]>([]);

  const savePayload = useMemo(() => {
    if ([name, coordinate, description].includes('') || triggers.length === 0) {
      return undefined;
    }

    if (type === OperationType.COMPARTMENT_UPDATE && file === undefined) {
      return undefined;
    }

    if (type === OperationType.SWITCH_PAGE && operations.length === 0) {
      return undefined;
    }

    return {
      name,
      description,
      triggers,
      operations,
      node: convertCoordinateToPath(coordinate),
    } as OperationGroupCreate;
  }, [name, description, triggers, operations, coordinate, file]);

  useEffect(() => {
    switch (currentStep) {
      case 0:
        enableNext(![name, coordinate, description].includes(''));
        break;
      case 2:
        enableNext(savePayload !== undefined);
        break;
      default:
        enableNext(true);
    }
  }, [currentStep, name, coordinate, description, savePayload]);

  useEffect(() => {
    enableSave(savePayload !== undefined);
  }, [savePayload]);

  const onReset = useCallback(() => {
    setCoordinate(operationGroup?.nodeParts.join('/') ?? '');

    setName(operationGroup?.name ?? '');
    setDescription(operationGroup?.description ?? '');
    setOperations(operationGroup?.operations ?? []);
    setTriggers(
      operationGroup?.triggers ?? [
        {
          payload: {
            datetime: moment().add(1, 'day').set('seconds', 0).format(),
          },
          type: TriggerType.TIME_BASED,
        },
      ],
    );
  }, [operationGroup]);

  /** on mount / unmount */
  useEffect(() => onReset(), [onReset]);

  const onClose = useCallback(() => {
    closeHandler();
  }, [closeHandler]);

  const onSave = useCallback(async () => {
    if (savePayload === undefined) {
      return;
    }

    saveHandler(savePayload, file);
    onClose();
  }, [saveHandler, onClose, operationGroup, savePayload]);

  const steps = useMemo(
    () => [
      {
        title: 'Operation Group',
        contentCallback: () => (
          <CenteredColumn width={'72%'}>
            <StyledSelectField
              native
              value={type}
              onChange={(e) => {
                setType(e.target.value as OperationType);
                setFile(undefined);
              }}
              label="Type"
            >
              <option key={0} value={OperationType.COMPARTMENT_UPDATE}>
                Compartment-Update
              </option>
              <option key={1} value={OperationType.SWITCH_PAGE}>
                Switch-Page
              </option>
            </StyledSelectField>
            <Box height={2} />
            <StyledTextField
              label={'Name'}
              value={name}
              name={'name'}
              onChange={(e) => setName(String(e.target.value))}
            />
            {hasAreasReadPermission && (
              <CoordinateInput trailingDelimiter={false} value={coordinate} onChange={setCoordinate} />
            )}
            <StyledTextField
              label={'Description'}
              value={description}
              name={'description'}
              onChange={(e) => setDescription(String(e.target.value))}
            />
          </CenteredColumn>
        ),
        description: <NamedCoordinateDescription name={name} coordinate={coordinate} />,
      },
      {
        title: 'Triggers',
        contentCallback: () => <TriggerForm triggers={triggers} onChange={setTriggers} />,
      },
      {
        title: 'Operations',
        contentCallback: () => (
          <OperationForm
            operations={operations}
            type={type}
            file={file}
            fetchLabels={fetchLabels}
            onChange={(o) => setOperations(o)}
            onChangeFile={setFile}
          />
        ),
      },
      {
        title: 'Overview',
        contentCallback: () => <OperationGroupCreateOverview operationGroup={savePayload!} file={file} />,
      },
    ],
    [name, coordinate, savePayload, type, file, description, triggers],
  );

  return <ContentPanel steps={steps} onSave={onSave} onReset={onReset} onCancel={onClose} />;
};
